<script setup lang="ts">
import {onMounted, onUnmounted, ref} from 'vue';
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {useI18n} from "vue-i18n";
import data from '@/data/sidebar.json';
import SidebarMenu from "@/shared/components/SidebarMenu.vue";

const {t} = useI18n();

const isExpanded = ref(false);

type Attribute = {
  all?: boolean;
  icon?: string;
  iconPrefix?: string;
  route: string;
  label: string;
  isGroup?: boolean;
  tooltip?: string;
  children?: Attribute[];
}

const icons: Attribute[] = data.icons;
const menu: Attribute[] = data.menu;

const toggleSidebar = () => {
  isExpanded.value = !isExpanded.value;
};

const expandSidebar = () => {
  if (!isExpanded.value) {
    isExpanded.value = true;
  }
};

// Detect clicks outside the sidebar to collapse it
const handleClickOutside = (event: MouseEvent) => {
  const sidebar = document.getElementById('sidebar');
  if (sidebar && !sidebar.contains(event.target as Node)) {
    isExpanded.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div class="sidebar-wrapper">
    <nav
      id="sidebar"
      :class="[
      'overflow-hidden top-0 left-0',
      'h-screen bg-quaternary text-white transition-width duration-300 flex flex-col justify-between overflow-y-auto',
      isExpanded ? 'w-60' : 'w-14'
    ]"
    >
      <ul>
        <li class="mb-4">
          <!-- Toggle Button -->
          <button
            @click="toggleSidebar"
            class="flex items-center justify-center w-full py-4 bg-primary hover:bg-opacity-80 focus:outline-none"
          >
            <font-awesome-icon :icon="isExpanded ? 'chevron-left' : 'chevron-right'"/>
          </button>
        </li>
        <li v-for="icon in icons" :key="icon.route">
          <div class="relative mb-2 transition group flex items-center">
            <RouterLink :to="{name: icon.route}" class="text-secondary w-full inline-flex items-center hover:bg-primary py-3" >
              <!-- Add tooltip to icons -->
              <FontAwesomeIcon
                :icon="[icon.iconPrefix, icon.icon]"
                class="flex items-center justify-center max-w-14 w-full focus:outline-none"
              />
              <span v-if="isExpanded">{{ t(`${icon.label}`) }}</span>
            </RouterLink>
            <!-- Tooltip for icons on hover -->
            <span v-if="!isExpanded" class="fixed left-12 bg-secondary text-secondary text-center text-sm p-1 rounded-s shadow opacity-0 transition-opacity hidden group-hover:flex group-hover:opacity-100">
              {{ t(`${icon.tooltip}`) }}
            </span>
          </div>
        </li>
      </ul>
      <br>
      <br>
      <ul class="flex flex-col justify-center items-center pb-4">
        <li v-for="item in menu" :key="item.route" class="w-full">
          <SidebarMenu
            :item="{
            ...item,
            all: isExpanded,
          }"
            @expandSidebar="expandSidebar"
          />
        </li>
      </ul>
    </nav>
  </div>
</template>

<style scoped lang="css">
/* CSS for smooth width transition */
.transition-width {
  transition-property: width;
}

.sidebar-wrapper {
  position: relative;
  height: 100vh;
  overflow: hidden;
}
</style>
